/**
 * Compatibility shim — replaces the deleted api-helper.ts.
 * Provides the same `apiCall` function and `api` convenience object that
 * consumers (services, hooks) previously imported from api-helper.
 * Internally delegates to the canonical apiService instance.
 *
 * Extracted from api.service.ts to keep that file ≤500 lines per LLM-friendly
 * architecture rules.
 */

import { ApiResponse, ApiException } from '@/shared/utils/api'
import { STORAGE_KEYS } from '@/shared/utils/constants'

import { apiService, type ApiCallOptions, type ApiHelperResponse } from './api.service'

/**
 * Helper to get the current locale for Accept-Language header.
 */
function getCurrentLocaleForAcceptLanguageHeader(): string {
  if (typeof window === 'undefined') return 'en'
  const stored = localStorage.getItem(STORAGE_KEYS.LANGUAGE)
  if (stored) return stored
  const browserLocale = navigator.language.split('-')[0]
  return ['en', 'es'].includes(browserLocale) ? browserLocale : 'en'
}

/**
 * Convert an ApiException thrown by apiService into the api-helper response shape.
 */
function convertExceptionToHelperResponseShape<T>(error: unknown): ApiHelperResponse<T> {
  if (error instanceof ApiException) {
    // Preserve `details` for both shapes: Zod field-error arrays AND
    // structured context objects (e.g. WRONG_FILE_FORMAT carries
    // `{ instructionsUrl, videoUrl, matchedRowCount, ... }`). The previous
    // implementation discarded non-array details to undefined, which made
    // backend rejection metadata invisible to UIs that needed it (issue
    // #305 rejection screen could not render the instructions/video links).
    let details:
      | Array<{ field: string; message: string; code?: string }>
      | Record<string, unknown>
      | undefined
    if (Array.isArray(error.details)) {
      details = error.details as Array<{ field: string; message: string; code?: string }>
    } else if (error.details && typeof error.details === 'object') {
      details = error.details as Record<string, unknown>
    } else {
      details = undefined
    }
    return {
      success: false,
      error: {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string error code means unset and must fall back
        code: error.code || 'HTTP_ERROR',
        message: error.message,
        statusCode: error.status ?? 0,
        ...(details !== undefined ? { details } : {}),
      },
    }
  }
  return {
    success: false,
    error: {
      code: 'NETWORK_ERROR',
      message: error instanceof Error ? error.message : 'Unknown network error',
      statusCode: 0,
    },
  }
}

/**
 * Low-level API call that returns the api-helper response shape instead of throwing.
 * Replaces the deleted `apiCall` from api-helper.ts.
 */
export async function apiCall<T = unknown>(
  endpoint: string,
  options: ApiCallOptions = {},
): Promise<ApiHelperResponse<T>> {
  const { method = 'GET', body, params, skipAuth = false } = options
  const locale = getCurrentLocaleForAcceptLanguageHeader()

  try {
    let result: ApiResponse<T>

    if (method === 'GET') {
      result = await apiService.get<T>(
        endpoint,
        { params, headers: { 'Accept-Language': locale } },
        skipAuth,
      )
    } else if (method === 'POST') {
      result = await apiService.post<T>(endpoint, body, { 'Accept-Language': locale }, skipAuth)
    } else if (method === 'PUT') {
      result = await apiService.put<T>(endpoint, body, { 'Accept-Language': locale }, skipAuth)
    } else if (method === 'PATCH') {
      result = await apiService.patch<T>(endpoint, body, { 'Accept-Language': locale }, skipAuth)
    } else if (method === 'DELETE') {
      // DELETE may carry a JSON body. Some endpoints require it — e.g.
      // DELETE /auth/mfa/disable reads re-auth `password`/`code` from the body;
      // dropping it returned a permanent 403 MFA_DISABLE_REAUTH_REQUIRED.
      // `body` is forwarded only when defined; otherwise the request is bodyless.
      result = await apiService.delete<T>(endpoint, { 'Accept-Language': locale }, skipAuth, body)
    } else {
      result = await apiService.get<T>(
        endpoint,
        { params, headers: { 'Accept-Language': locale } },
        skipAuth,
      )
    }

    // apiService returns { data, success, message } — convert to api-helper shape
    return {
      success: result.success,
      data: result.data,
      message: result.message,
    }
  } catch (error) {
    return convertExceptionToHelperResponseShape<T>(error)
  }
}

/**
 * Convenience methods for common HTTP verbs — same interface as the old api-helper `api` object.
 * Replaces the deleted `api` export from api-helper.ts.
 */
export const api = {
  get: <T = unknown>(
    endpoint: string,
    params?: Record<string, string | number | boolean | null | undefined>,
    skipAuth = false,
  ) => apiCall<T>(endpoint, { method: 'GET', params, skipAuth }),

  post: <T = unknown>(endpoint: string, body?: unknown, skipAuth = false) =>
    apiCall<T>(endpoint, { method: 'POST', body, skipAuth }),

  put: <T = unknown>(endpoint: string, body?: unknown, skipAuth = false) =>
    apiCall<T>(endpoint, { method: 'PUT', body, skipAuth }),

  // `body` is forwarded for DELETE endpoints that require a JSON payload
  // (e.g. DELETE /auth/mfa/disable re-auth password/code). Previously dropped,
  // which made disabling MFA impossible (permanent 403 MFA_DISABLE_REAUTH_REQUIRED).
  delete: <T = unknown>(endpoint: string, body?: unknown, skipAuth = false) =>
    apiCall<T>(endpoint, { method: 'DELETE', body, skipAuth }),

  patch: <T = unknown>(endpoint: string, body?: unknown, skipAuth = false) =>
    apiCall<T>(endpoint, { method: 'PATCH', body, skipAuth }),
}

/**
 * Error helper — replaces the deleted isApiError from api-helper.ts.
 */
export function isApiError<T>(
  response: ApiHelperResponse<T>,
): response is ApiHelperResponse<T> & { success: false } {
  return !response.success
}

/**
 * Unwrap helper — replaces the deleted unwrapApiResponse from api-helper.ts.
 * Throws an ApiException so callers (and downstream UI) can read message + code + details.
 */
export function unwrapApiResponse<T>(response: ApiHelperResponse<T>): T {
  if (isApiError(response)) {
    throwBackendError(response, 'API_CALL_FAILED')
  }
  return response.data as T
}

/**
 * Re-throw the backend error preserving message, code, statusCode AND field-level details.
 * Use this in service-layer code right after detecting `response.success === false`.
 *
 * `api.post/get/...` never throws — it returns `{ success: false, error: {...} }`. Callers
 * must explicitly bubble the error up. Hardcoding a generic message here would discard
 * the backend's localized message and Zod field-level errors that the UI relies on.
 */
export function throwBackendError(
  response: ApiHelperResponse<unknown>,
  fallbackCode = 'API_ERROR',
): never {
  const backendError = response.error
  throw new ApiException({
    status: backendError?.statusCode ?? 400,
    message: backendError?.message ?? '',
    code: backendError?.code ?? fallbackCode,
    details: backendError?.details,
  })
}
